/**
 * Wait for a background job to finish and hand back what it produced.
 *
 * For a request that answers with a job id instead of doing the work (POST
 * /reevaluate with `asJob`): poll GET /async-jobs/{id} until the job is
 * terminal. A job the reaper requeued after its worker died goes back to
 * `queued` — that is not an outcome, so keep waiting.
 *
 * The app passes its own fetcher, so each app's auth and base URL apply.
 * See docs/FeatureDesigns/solution-analysis/reevaluation-job-target.md.
 */

export interface PolledJobView {
  status: 'queued' | 'running' | 'completed' | 'failed' | 'dead';
  result?: unknown;
  lastError?: { message: string; httpStatus?: number };
}

export interface WaitForAsyncJobOptions {
  /** Between polls. */
  intervalMs?: number;
  /** Give up waiting (the job carries on) after this long. */
  maxWaitMs?: number;
  /** Polls that may fail in a row (network blip, a cold instance) before we stop. */
  maxConsecutiveErrors?: number;
  /** Test seam. */
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/** The job ran and failed; `message` is the server's own reason, `status` the
 *  HTTP status the same error would have had inline (4xx = a refusal). */
export class AsyncJobFailedError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AsyncJobFailedError';
    this.status = status;
  }
}

/** We stopped waiting; the job may still finish. */
export class AsyncJobWaitTimeoutError extends Error {
  constructor() {
    super('This is taking longer than usual. It is still running — check back in a few minutes.');
    this.name = 'AsyncJobWaitTimeoutError';
  }
}

export async function waitForAsyncJob<T>(
  fetchJob: (jobId: string) => Promise<{ job: PolledJobView | null }>,
  jobId: string,
  opts: WaitForAsyncJobOptions = {},
): Promise<T> {
  const intervalMs = opts.intervalMs ?? 3000;
  // Two full attempts (4 min timeout each) plus a reclaim window, with room.
  const maxWaitMs = opts.maxWaitMs ?? 15 * 60_000;
  const maxErrors = opts.maxConsecutiveErrors ?? 5;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));
  const now = opts.now ?? Date.now;

  const start = now();
  let errors = 0;
  for (;;) {
    await sleep(intervalMs);
    let job: PolledJobView | null;
    try {
      job = (await fetchJob(jobId)).job;
      errors = 0;
    } catch (err) {
      if (++errors >= maxErrors) throw err;
      continue;
    }
    if (job?.status === 'completed') return job.result as T;
    if (job?.status === 'failed' || job?.status === 'dead') {
      throw new AsyncJobFailedError(job.lastError?.message || 'The job failed', job.lastError?.httpStatus);
    }
    if (now() - start > maxWaitMs) throw new AsyncJobWaitTimeoutError();
  }
}
